import React, { FC, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { db } from '../../firebase';
import { collection, addDoc, getDocs, query, where, doc,
         updateDoc, deleteDoc, serverTimestamp, orderBy } from 'firebase/firestore';
import { UserAuth } from '../../AuthContext';
import { Box, Flex, Text, Heading, Spinner, Center } from '@chakra-ui/react';

const F = { ui:"'Rajdhani','Arial',sans-serif", mono:"'Share Tech Mono',monospace" };
const C = { bg:'#080A0E',card:'#131720',border:'#1E2330',gold:'#D4A017',teal:'#19E6CF',
  white:'#F0F4FF',ink:'#C8D0E0',dim:'#6B7590',green:'#22C55E',red:'#EF4444' };
const inp: React.CSSProperties = { display:'block',width:'100%',padding:'10px 12px',
  background:'#0D1118',border:`1px solid #1E2330`,borderRadius:6,color:'#F0F4FF',
  fontSize:14,fontFamily:F.ui,boxSizing:'border-box' as any };
const lbl: React.CSSProperties = { display:'block',fontFamily:F.mono,fontSize:10,
  color:C.teal,letterSpacing:'0.15em',marginBottom:4,textTransform:'uppercase' as const };

// ── TABS ───────────────────────────────────────────────────────────────────
const TABS = [
  { id:'profile',    label:'Profile',    icon:'👤' },
  { id:'venues',     label:'Venues',     icon:'📍' },
  { id:'structures', label:'Structures', icon:'🃏' },
  { id:'charities',  label:'Charities',  icon:'❤️' },
  { id:'licenses',   label:'Licenses',   icon:'📋' },
  { id:'series',     label:'Recurring',  icon:'🔄' },
];

// ── VENUE TYPES ────────────────────────────────────────────────────────────
const VENUE_TYPES = ['Home','Hotel','Bar / Restaurant','Event Space',
  'Casino','Corporate Office','Community Centre','Other'];

// ── LICENSE TYPES ──────────────────────────────────────────────────────────
const LICENSE_TYPES = ['Private Home Game (no license)','Charitable Gaming License',
  'Special Occasion Permit','Licensed Gaming Establishment',
  'Corporate Event Permit','Casino / Commercial','Other'];

// ── PAYOUT TEMPLATES ───────────────────────────────────────────────────────
const PAYOUT_TEMPLATES: Record<string, {position:string;pct:number}[]> = {
  'Top 3 (50/30/20)': [{position:'1st',pct:50},{position:'2nd',pct:30},{position:'3rd',pct:20}],
  'Top 3 (60/25/15)': [{position:'1st',pct:60},{position:'2nd',pct:25},{position:'3rd',pct:15}],
  'Top 5':            [{position:'1st',pct:40},{position:'2nd',pct:25},{position:'3rd',pct:17},{position:'4th',pct:11},{position:'5th',pct:7}],
  'Top 10':           [{position:'1st',pct:30},{position:'2nd',pct:20},{position:'3rd',pct:14},{position:'4th',pct:10},{position:'5th',pct:8},{position:'6th',pct:6},{position:'7th',pct:5},{position:'8th',pct:4},{position:'9th',pct:2},{position:'10th',pct:1}],
  'Winner Take All':  [{position:'1st',pct:100}],
};

// ── BLIND PRESETS ──────────────────────────────────────────────────────────
const DEFAULT_BLIND_LEVELS = [
  '25/50','50/100','75/150','100/200/25','150/300/25','200/400/50',
  '300/600/75','400/800/100','500/1000/100','600/1200/200','800/1600/200',
  '1000/2000/300','1500/3000/300','2000/4000/500','BREAK 15min',
  '3000/6000/500','4000/8000/1000','5000/10000/1000',
];

// ─────────────────────────────────────────────────────────────────────────
const TDControlCenter: FC = () => {
  const { user } = UserAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('profile');
  const [loading, setLoading] = useState(false);

  // ── DATA ─────────────────────────────────────────────────────────────
  const [venues, setVenues] = useState<any[]>([]);
  const [structures, setStructures] = useState<any[]>([]);
  const [charities, setCharities] = useState<any[]>([]);
  const [licenses, setLicenses] = useState<any[]>([]);
  const [tdProfile, setTdProfile] = useState<any>({});

  // ── FORM STATES ───────────────────────────────────────────────────────
  const [showVenueForm, setShowVenueForm] = useState(false);
  const [showStructureForm, setShowStructureForm] = useState(false);
  const [showCharityForm, setShowCharityForm] = useState(false);
  const [showLicenseForm, setShowLicenseForm] = useState(false);
  const [tdSeries, setTdSeries] = useState<any[]>([]);

  const [venueForm, setVenueForm] = useState<any>({ name:'',type:'Home',address:'',parking:'',food_drinks:'',amenities:'',capacity:'',access_notes:'',city:'',province:'' });
  const [structureForm, setStructureForm] = useState<any>({ name:'',starting_chips:10000,blind_level_duration:20,rebuy_allowed:false,rebuy_cutoff_level:6,rebuy_price:0,rebuy_chips:0,addon_allowed:false,addon_level:6,addon_price:0,addon_chips:0,addon_instructions:'See TD for add-on chips',late_reg_level:6,payout_template:'Top 3 (50/30/20)',charity_pct:0,levels:DEFAULT_BLIND_LEVELS.join('\n') });
  const [charityForm, setCharityForm] = useState<any>({ name:'',registration_number:'',contact:'',default_pct:10,notes:'' });
  const [licenseForm, setLicenseForm] = useState<any>({ type:'Private Home Game (no license)',number:'',authority:'',jurisdiction:'',expires:'' });

  useEffect(() => {
    if (!user?.uid) return;
    loadAll();
  }, [user?.uid]);

  const loadAll = async () => {
    setLoading(true);
    try {
      const [vSnap, sSnap, cSnap, lSnap] = await Promise.all([
        getDocs(query(collection(db, 'users', user!.uid, 'venues'), orderBy('created_at','desc'))),
        getDocs(query(collection(db, 'users', user!.uid, 'structures'), orderBy('created_at','desc'))),
        getDocs(query(collection(db, 'users', user!.uid, 'charities'), orderBy('created_at','desc'))),
        getDocs(query(collection(db, 'users', user!.uid, 'licenses'), orderBy('created_at','desc'))),
      ]);
      setVenues(vSnap.docs.map(d => ({ id:d.id, ...d.data() })));
      setStructures(sSnap.docs.map(d => ({ id:d.id, ...d.data() })));
      setCharities(cSnap.docs.map(d => ({ id:d.id, ...d.data() })));
      setLicenses(lSnap.docs.map(d => ({ id:d.id, ...d.data() })));
    } catch (e) { console.error(e); }
    setLoading(false);
  };

  const saveVenue = async () => {
    if (!venueForm.name) return;
    await addDoc(collection(db, 'users', user!.uid, 'venues'), {
      ...venueForm, created_at: serverTimestamp()
    });
    setShowVenueForm(false);
    setVenueForm({ name:'',type:'Home',address:'',parking:'',food_drinks:'',amenities:'',capacity:'',access_notes:'',city:'',province:'' });
    loadAll();
  };

  const saveStructure = async () => {
    if (!structureForm.name) return;
    const payoutPcts = PAYOUT_TEMPLATES[structureForm.payout_template] ?? [];
    await addDoc(collection(db, 'users', user!.uid, 'structures'), {
      ...structureForm,
      addon_instructions: structureForm.addon_instructions || 'See TD for add-on chips',
      payout_entries: payoutPcts,
      levels_array: structureForm.levels.split('\n').filter((l:string) => l.trim()),
      created_at: serverTimestamp()
    });
    setShowStructureForm(false);
    loadAll();
  };

  const saveCharity = async () => {
    if (!charityForm.name) return;
    await addDoc(collection(db, 'users', user!.uid, 'charities'), {
      ...charityForm, created_at: serverTimestamp()
    });
    setShowCharityForm(false);
    setCharityForm({ name:'',registration_number:'',contact:'',default_pct:10,notes:'' });
    loadAll();
  };

  const saveLicense = async () => {
    if (!licenseForm.type) return;
    const expiryDate = licenseForm.expires ? new Date(licenseForm.expires) : null;
    const daysUntilExpiry = expiryDate
      ? Math.ceil((expiryDate.getTime() - Date.now()) / 86400000) : null;
    await addDoc(collection(db, 'users', user!.uid, 'licenses'), {
      ...licenseForm, days_until_expiry: daysUntilExpiry,
      created_at: serverTimestamp()
    });
    setShowLicenseForm(false);
    setLicenseForm({ type:'Private Home Game (no license)',number:'',authority:'',jurisdiction:'',expires:'' });
    loadAll();
  };

  const deleteItem = async (subcol: string, id: string) => {
    await deleteDoc(doc(db, 'users', user!.uid, subcol, id));
    loadAll();
  };

  const cardStyle = { background:C.card, border:`1px solid ${C.border}`,
    borderRadius:10, overflow:'hidden' as const, marginBottom:12 };
  const headerStyle = { background:'#0D1520', padding:'10px 16px',
    borderBottom:`1px solid ${C.border}` };

  if (!user) return <Center h="400px"><Text color="gray.400">Sign in to access TD Control Center.</Text></Center>;

  return (
    <Box maxW="900px" mx="auto" px={4} py={6} fontFamily={F.ui}>

      {/* Header */}
      <Flex align="center" justify="space-between" mb={6}>
        <Box>
          <Heading style={{ fontFamily:F.ui, color:C.white, fontSize:28, marginBottom:4 }}>
            TD Control Center
          </Heading>
          <Text style={{ fontFamily:F.mono, fontSize:11, color:C.teal, letterSpacing:'0.15em' }}>
            TOURNAMENT DIRECTOR OPERATING SYSTEM
          </Text>
        </Box>
        <Box as="button" px={4} py={2} bg="#052E16" border="1px solid #166534"
          borderRadius={8} fontSize="13px" fontWeight="700" color="#86EFAC"
          fontFamily={F.ui} cursor="pointer"
          onClick={() => navigate('/create-event/')}>
          + New Event →
        </Box>
      </Flex>

      {/* Tabs */}
      <Flex gap={2} mb={6} flexWrap="wrap">
        {TABS.map(tab => (
          <Box key={tab.id} as="button" px={4} py={2}
            bg={activeTab === tab.id ? '#0D1F1E' : C.card}
            border={`1px solid ${activeTab === tab.id ? C.teal : C.border}`}
            borderRadius={8} fontSize="13px" fontWeight="700"
            color={activeTab === tab.id ? C.teal : C.dim}
            fontFamily={F.ui} cursor="pointer"
            onClick={() => setActiveTab(tab.id)}>
            {tab.icon} {tab.label}
          </Box>
        ))}
      </Flex>

      {loading && <Center py={8}><Spinner color="teal.400" /></Center>}

      {/* ── VENUES TAB ── */}
      {activeTab === 'venues' && !loading && (
        <Box>
          <Flex justify="space-between" align="center" mb={4}>
            <Text style={{ fontFamily:F.mono, fontSize:11, color:C.dim, letterSpacing:'0.15em' }}>
              {venues.length} VENUE{venues.length !== 1 ? 'S' : ''} SAVED
            </Text>
            <Box as="button" px={4} py={2} bg="#0D1F1E" border={`1px solid ${C.teal}`}
              borderRadius={6} fontSize="12px" fontWeight="700" color={C.teal}
              fontFamily={F.ui} cursor="pointer"
              onClick={() => setShowVenueForm(v => !v)}>
              {showVenueForm ? '✕ Cancel' : '+ Add Venue'}
            </Box>
          </Flex>

          {showVenueForm && (
            <Box style={cardStyle} mb={4}>
              <Box style={headerStyle}>
                <Text style={{ fontFamily:F.mono, fontSize:11, color:C.teal, letterSpacing:'0.2em' }}>
                  NEW VENUE
                </Text>
              </Box>
              <Box p={4}>
                <Flex direction="column" gap={4}>
                  <Flex gap={3}>
                    <Box flex={2}>
                      <label style={lbl}>Venue Name *</label>
                      <input style={inp} placeholder="e.g. Wayne's Place / Hilton Garden Inn"
                        value={venueForm.name}
                        onChange={e => setVenueForm((f:any) => ({...f,name:e.target.value}))} />
                    </Box>
                    <Box flex={1}>
                      <label style={lbl}>Type</label>
                      <select style={{...inp,cursor:'pointer'}} value={venueForm.type}
                        onChange={e => setVenueForm((f:any) => ({...f,type:e.target.value}))}>
                        {VENUE_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
                      </select>
                    </Box>
                  </Flex>
                  <Flex gap={3}>
                    <Box flex={2}>
                      <label style={lbl}>Street Address (shown after RSVP confirmed)</label>
                      <input style={inp} placeholder="123 Main St"
                        value={venueForm.address}
                        onChange={e => setVenueForm((f:any) => ({...f,address:e.target.value}))} />
                    </Box>
                    <Box flex={1}>
                      <label style={lbl}>Capacity</label>
                      <input style={inp} type="number" placeholder="Max players"
                        value={venueForm.capacity}
                        onChange={e => setVenueForm((f:any) => ({...f,capacity:e.target.value}))} />
                    </Box>
                  </Flex>
                  <Flex gap={3}>
                    <Box flex={1}>
                      <label style={lbl}>City</label>
                      <input style={inp} placeholder="e.g. Winnipeg"
                        value={venueForm.city}
                        onChange={e => setVenueForm((f:any) => ({...f,city:e.target.value}))} />
                    </Box>
                    <Box flex={1}>
                      <label style={lbl}>Province / State</label>
                      <input style={inp} placeholder="e.g. MB"
                        value={venueForm.province}
                        onChange={e => setVenueForm((f:any) => ({...f,province:e.target.value}))} />
                    </Box>
                  </Flex>
                  {[
                    {label:'Parking',field:'parking',ph:'e.g. Free street parking on Oak St'},
                    {label:'Food & Drinks',field:'food_drinks',ph:'e.g. BYOB, snacks provided'},
                    {label:'Amenities',field:'amenities',ph:'e.g. Professional felt tables, clay chips'},
                    {label:'Access Notes (encrypted — not shown publicly)',field:'access_notes',ph:'e.g. Buzz #302, park in back lot'},
                  ].map(f => (
                    <Box key={f.field}>
                      <label style={lbl}>{f.label}</label>
                      <Box as="textarea" rows={2} style={{...inp,resize:'none' as any}}
                        placeholder={f.ph} value={venueForm[f.field]}
                        onChange={(e:any) => setVenueForm((vf:any) => ({...vf,[f.field]:e.target.value}))} />
                    </Box>
                  ))}
                  <Box as="button" px={5} py={3} bg="#052E16" border="1px solid #166534"
                    borderRadius={8} fontSize="14px" fontWeight="700" color="#86EFAC"
                    fontFamily={F.ui} cursor="pointer" onClick={saveVenue}>
                    Save Venue
                  </Box>
                </Flex>
              </Box>
            </Box>
          )}

          {venues.map(v => (
            <Box key={v.id} style={cardStyle}>
              <Flex style={headerStyle} justify="space-between" align="center">
                <Flex align="center" gap={3}>
                  <Text style={{ fontFamily:F.ui, fontSize:16, fontWeight:700, color:C.white }}>
                    {v.name}
                  </Text>
                  <Box px={2} py={0} bg={C.card} border={`1px solid ${C.border}`} borderRadius={4}>
                    <Text style={{ fontFamily:F.mono, fontSize:9, color:C.dim }}>{v.type}</Text>
                  </Box>
                </Flex>
                <Box as="button" px={2} py={1} bg="transparent" border="none"
                  fontSize="12px" color={C.red} cursor="pointer"
                  onClick={() => deleteItem('venues', v.id)}>✕</Box>
              </Flex>
              <Flex p={4} gap={6} flexWrap="wrap">
                {v.city && <Box><Text style={{ fontFamily:F.mono, fontSize:10, color:C.dim, letterSpacing:'0.12em' }}>LOCATION</Text><Text style={{ fontFamily:F.ui, fontSize:13, color:C.ink }}>{v.city}{v.province ? `, ${v.province}` : ''}</Text></Box>}
                {v.capacity && <Box><Text style={{ fontFamily:F.mono, fontSize:10, color:C.dim, letterSpacing:'0.12em' }}>CAPACITY</Text><Text style={{ fontFamily:F.ui, fontSize:13, color:C.ink }}>{v.capacity} players</Text></Box>}
                {v.parking && <Box flex={1} minW="120px"><Text style={{ fontFamily:F.mono, fontSize:10, color:C.dim, letterSpacing:'0.12em' }}>PARKING</Text><Text style={{ fontFamily:F.ui, fontSize:13, color:C.ink }}>{v.parking}</Text></Box>}
                {v.amenities && <Box flex={1} minW="120px"><Text style={{ fontFamily:F.mono, fontSize:10, color:C.dim, letterSpacing:'0.12em' }}>AMENITIES</Text><Text style={{ fontFamily:F.ui, fontSize:13, color:C.ink }}>{v.amenities}</Text></Box>}
              </Flex>
            </Box>
          ))}

          {venues.length === 0 && !showVenueForm && (
            <Box style={cardStyle} p={6} textAlign="center">
              <Text style={{ fontSize:36, marginBottom:8 }}>📍</Text>
              <Text style={{ fontFamily:F.ui, fontSize:15, color:C.dim }}>
                No venues saved yet. Add your first venue to reuse it across events.
              </Text>
            </Box>
          )}
        </Box>
      )}

      {/* ── STRUCTURES TAB ── */}
      {activeTab === 'structures' && !loading && (
        <Box>
          <Flex justify="space-between" align="center" mb={4}>
            <Text style={{ fontFamily:F.mono, fontSize:11, color:C.dim, letterSpacing:'0.15em' }}>
              {structures.length} STRUCTURE{structures.length !== 1 ? 'S' : ''} SAVED
            </Text>
            <Box as="button" px={4} py={2} bg="#0D1F1E" border={`1px solid ${C.teal}`}
              borderRadius={6} fontSize="12px" fontWeight="700" color={C.teal}
              fontFamily={F.ui} cursor="pointer"
              onClick={() => setShowStructureForm(v => !v)}>
              {showStructureForm ? '✕ Cancel' : '+ New Structure'}
            </Box>
          </Flex>

          {showStructureForm && (
            <Box style={cardStyle} mb={4}>
              <Box style={headerStyle}>
                <Text style={{ fontFamily:F.mono, fontSize:11, color:C.teal, letterSpacing:'0.2em' }}>
                  NEW TOURNAMENT STRUCTURE
                </Text>
              </Box>
              <Box p={4}>
                <Flex direction="column" gap={4}>
                  <Box>
                    <label style={lbl}>Structure Name *</label>
                    <input style={inp} placeholder="e.g. Wayne's Thursday Night Standard"
                      value={structureForm.name}
                      onChange={e => setStructureForm((f:any) => ({...f,name:e.target.value}))} />
                  </Box>
                  <Flex gap={3} flexWrap="wrap">
                    <Box flex={1} minW="120px">
                      <label style={lbl}>Starting Chips</label>
                      <input style={inp} type="number" value={structureForm.starting_chips}
                        onChange={e => setStructureForm((f:any) => ({...f,starting_chips:parseInt(e.target.value)||0}))} />
                    </Box>
                    <Box flex={1} minW="120px">
                      <label style={lbl}>Level Duration (min)</label>
                      <input style={inp} type="number" value={structureForm.blind_level_duration}
                        onChange={e => setStructureForm((f:any) => ({...f,blind_level_duration:parseInt(e.target.value)||20}))} />
                    </Box>
                    <Box flex={1} minW="120px">
                      <label style={lbl}>Late Reg Until Level</label>
                      <input style={inp} type="number" value={structureForm.late_reg_level}
                        onChange={e => setStructureForm((f:any) => ({...f,late_reg_level:parseInt(e.target.value)||6}))} />
                    </Box>
                  </Flex>

                  {/* Blind levels */}
                  <Box>
                    <label style={lbl}>Blind Structure (one level per line: SB/BB or SB/BB/ANTE or BREAK Xmin)</label>
                    <Box as="textarea" rows={10} style={{...inp,resize:'none' as any,fontFamily:F.mono,fontSize:12}}
                      value={structureForm.levels}
                      onChange={(e:any) => setStructureForm((f:any) => ({...f,levels:e.target.value}))} />
                    <Text style={{ fontFamily:F.ui, fontSize:11, color:C.dim, marginTop:4 }}>
                      Format: 25/50 · 100/200/25 · BREAK 15min
                    </Text>
                  </Box>

                  {/* Rebuys */}
                  <Box bg="#0A0A0A" borderRadius={6} p={3}>
                    <Flex align="center" gap={3} mb={3} cursor="pointer"
                      onClick={() => setStructureForm((f:any) => ({...f,rebuy_allowed:!f.rebuy_allowed}))}>
                      <Box w="20px" h="20px" borderRadius={4}
                        bg={structureForm.rebuy_allowed ? C.gold : 'transparent'}
                        border={`2px solid ${structureForm.rebuy_allowed ? C.gold : C.border}`}
                        display="flex" alignItems="center" justifyContent="center">
                        {structureForm.rebuy_allowed && <Text style={{ fontSize:12,color:'#000',fontWeight:700 }}>✓</Text>}
                      </Box>
                      <Text style={{ fontFamily:F.ui, fontSize:14, color:C.ink }}>Rebuys allowed</Text>
                    </Flex>
                    {structureForm.rebuy_allowed && (
                      <Flex gap={3} flexWrap="wrap">
                        {[{label:'Until Level',field:'rebuy_cutoff_level'},{label:'Rebuy Price ($)',field:'rebuy_price'},{label:'Rebuy Chips',field:'rebuy_chips'}].map(f => (
                          <Box key={f.field} flex={1} minW="100px">
                            <label style={lbl}>{f.label}</label>
                            <input style={inp} type="number" value={structureForm[f.field]}
                              onChange={e => setStructureForm((sf:any) => ({...sf,[f.field]:parseInt(e.target.value)||0}))} />
                          </Box>
                        ))}
                      </Flex>
                    )}
                  </Box>

                  {/* Add-ons */}
                  <Box bg="#0A0A0A" borderRadius={6} p={3}>
                    <Flex align="center" gap={3} mb={3} cursor="pointer"
                      onClick={() => setStructureForm((f:any) => ({...f,addon_allowed:!f.addon_allowed}))}>
                      <Box w="20px" h="20px" borderRadius={4}
                        bg={structureForm.addon_allowed ? C.teal : 'transparent'}
                        border={`2px solid ${structureForm.addon_allowed ? C.teal : C.border}`}
                        display="flex" alignItems="center" justifyContent="center">
                        {structureForm.addon_allowed && <Text style={{ fontSize:12,color:'#000',fontWeight:700 }}>✓</Text>}
                      </Box>
                      <Text style={{ fontFamily:F.ui, fontSize:14, color:C.ink }}>Add-on available</Text>
                    </Flex>
                    {structureForm.addon_allowed && (
                      <Flex gap={3} flexWrap="wrap">
                        {[{label:'At End of Level',field:'addon_level'},{label:'Add-on Price ($)',field:'addon_price'},{label:'Add-on Chips',field:'addon_chips'}].map(f => (
                          <Box key={f.field} flex={1} minW="100px">
                            <label style={lbl}>{f.label}</label>
                            <input style={inp} type="number" value={structureForm[f.field]}
                              onChange={e => setStructureForm((sf:any) => ({...sf,[f.field]:parseInt(e.target.value)||0}))} />
                          </Box>
                        ))}
                        <Box flex={2} minW="200px">
                          <label style={lbl}>Add-on Instructions (shown on clock)</label>
                          <input style={inp}
                            placeholder="e.g. Go to admin table for add-on chips"
                            value={structureForm.addon_instructions}
                            onChange={e => setStructureForm((sf:any) => ({...sf,addon_instructions:e.target.value}))} />
                          <Text style={{ fontFamily:F.ui, fontSize:11, color:C.dim, marginTop:4 }}>
                            This message displays on the clock face when the add-on period opens.
                          </Text>
                        </Box>
                      </Flex>
                    )}
                  </Box>

                  {/* Payout template */}
                  <Box>
                    <label style={lbl}>Payout Template</label>
                    <select style={{...inp,cursor:'pointer'}} value={structureForm.payout_template}
                      onChange={e => setStructureForm((f:any) => ({...f,payout_template:e.target.value}))}>
                      {Object.keys(PAYOUT_TEMPLATES).map(t => <option key={t} value={t}>{t}</option>)}
                    </select>
                    <Text style={{ fontFamily:F.ui, fontSize:11, color:C.dim, marginTop:4 }}>
                      {PAYOUT_TEMPLATES[structureForm.payout_template]?.map((p:{position:string;pct:number}) => `${p.position}: ${p.pct}%`).join(' · ')}
                    </Text>
                  </Box>

                  {/* Charity % */}
                  <Box>
                    <label style={lbl}>Charity % of Prize Pool (0 = none)</label>
                    <input style={inp} type="number" min="0" max="100"
                      placeholder="e.g. 10 for 10%"
                      value={structureForm.charity_pct}
                      onChange={e => setStructureForm((f:any) => ({...f,charity_pct:parseInt(e.target.value)||0}))} />
                    {structureForm.charity_pct > 0 && (
                      <Text style={{ fontFamily:F.ui, fontSize:12, color:C.gold, marginTop:4 }}>
                        {structureForm.charity_pct}% of the prize pool will go to the selected charity.
                        Player pool will be {100-structureForm.charity_pct}% of total.
                      </Text>
                    )}
                  </Box>

                  <Box as="button" px={5} py={3} bg="#052E16" border="1px solid #166534"
                    borderRadius={8} fontSize="14px" fontWeight="700" color="#86EFAC"
                    fontFamily={F.ui} cursor="pointer" onClick={saveStructure}>
                    Save Structure
                  </Box>
                </Flex>
              </Box>
            </Box>
          )}

          {structures.map(s => (
            <Box key={s.id} style={cardStyle}>
              <Flex style={headerStyle} justify="space-between" align="center">
                <Flex align="center" gap={3}>
                  <Text style={{ fontFamily:F.ui, fontSize:16, fontWeight:700, color:C.white }}>{s.name}</Text>
                  {s.charity_pct > 0 && <Box px={2} py={0} bg="#1A0F00" border={`1px solid ${C.gold}`} borderRadius={4}><Text style={{ fontFamily:F.mono, fontSize:9, color:C.gold }}>❤️ {s.charity_pct}% CHARITY</Text></Box>}
                </Flex>
                <Box as="button" px={2} py={1} bg="transparent" border="none" fontSize="12px" color={C.red} cursor="pointer" onClick={() => deleteItem('structures', s.id)}>✕</Box>
              </Flex>
              <Flex p={4} gap={5} flexWrap="wrap">
                <Box><Text style={{ fontFamily:F.mono, fontSize:10, color:C.dim, letterSpacing:'0.12em' }}>CHIPS</Text><Text style={{ fontFamily:F.ui, fontSize:14, fontWeight:700, color:C.white }}>{s.starting_chips?.toLocaleString()}</Text></Box>
                <Box><Text style={{ fontFamily:F.mono, fontSize:10, color:C.dim, letterSpacing:'0.12em' }}>LEVELS</Text><Text style={{ fontFamily:F.ui, fontSize:14, fontWeight:700, color:C.white }}>{s.blind_level_duration}min</Text></Box>
                <Box><Text style={{ fontFamily:F.mono, fontSize:10, color:C.dim, letterSpacing:'0.12em' }}>LATE REG</Text><Text style={{ fontFamily:F.ui, fontSize:14, fontWeight:700, color:C.white }}>Until L{s.late_reg_level}</Text></Box>
                {s.rebuy_allowed && <Box><Text style={{ fontFamily:F.mono, fontSize:10, color:C.dim, letterSpacing:'0.12em' }}>REBUYS</Text><Text style={{ fontFamily:F.ui, fontSize:14, fontWeight:700, color:C.gold }}>${s.rebuy_price} until L{s.rebuy_cutoff_level}</Text></Box>}
                {s.addon_allowed && <Box><Text style={{ fontFamily:F.mono, fontSize:10, color:C.dim, letterSpacing:'0.12em' }}>ADD-ON</Text><Text style={{ fontFamily:F.ui, fontSize:14, fontWeight:700, color:C.teal }}>${s.addon_price} at L{s.addon_level}</Text></Box>}
                <Box><Text style={{ fontFamily:F.mono, fontSize:10, color:C.dim, letterSpacing:'0.12em' }}>PAYOUT</Text><Text style={{ fontFamily:F.ui, fontSize:14, fontWeight:700, color:C.white }}>{s.payout_template}</Text></Box>
              </Flex>
            </Box>
          ))}

          {structures.length === 0 && !showStructureForm && (
            <Box style={cardStyle} p={6} textAlign="center">
              <Text style={{ fontSize:36, marginBottom:8 }}>🃏</Text>
              <Text style={{ fontFamily:F.ui, fontSize:15, color:C.dim }}>
                No structures saved yet. Build your first tournament structure and reuse it across events.
              </Text>
            </Box>
          )}
        </Box>
      )}

      {/* ── CHARITIES TAB ── */}
      {activeTab === 'charities' && !loading && (
        <Box>
          <Flex justify="space-between" align="center" mb={4}>
            <Text style={{ fontFamily:F.mono, fontSize:11, color:C.dim, letterSpacing:'0.15em' }}>
              {charities.length} CHARIT{charities.length !== 1 ? 'IES' : 'Y'} SAVED
            </Text>
            <Box as="button" px={4} py={2} bg="#0D1F1E" border={`1px solid ${C.teal}`}
              borderRadius={6} fontSize="12px" fontWeight="700" color={C.teal}
              fontFamily={F.ui} cursor="pointer"
              onClick={() => setShowCharityForm(v => !v)}>
              {showCharityForm ? '✕ Cancel' : '+ Add Charity'}
            </Box>
          </Flex>

          {showCharityForm && (
            <Box style={cardStyle} mb={4}>
              <Box style={headerStyle}>
                <Text style={{ fontFamily:F.mono, fontSize:11, color:C.teal, letterSpacing:'0.2em' }}>NEW CHARITY</Text>
              </Box>
              <Box p={4}>
                <Flex direction="column" gap={4}>
                  <Box>
                    <label style={lbl}>Charity Name *</label>
                    <input style={inp} placeholder="e.g. Canadian Cancer Society"
                      value={charityForm.name} onChange={e => setCharityForm((f:any) => ({...f,name:e.target.value}))} />
                  </Box>
                  <Flex gap={3}>
                    <Box flex={1}>
                      <label style={lbl}>Registration Number</label>
                      <input style={inp} placeholder="e.g. BN 123456789 RR0001"
                        value={charityForm.registration_number} onChange={e => setCharityForm((f:any) => ({...f,registration_number:e.target.value}))} />
                    </Box>
                    <Box flex={1}>
                      <label style={lbl}>Default % of Prize Pool</label>
                      <input style={inp} type="number" min="0" max="100" value={charityForm.default_pct}
                        onChange={e => setCharityForm((f:any) => ({...f,default_pct:parseInt(e.target.value)||0}))} />
                    </Box>
                  </Flex>
                  <Box>
                    <label style={lbl}>Contact Person / Notes</label>
                    <Box as="textarea" rows={2} style={{...inp,resize:'none' as any}}
                      placeholder="Contact name, cheque payable to, etc."
                      value={charityForm.notes} onChange={(e:any) => setCharityForm((f:any) => ({...f,notes:e.target.value}))} />
                  </Box>
                  <Box as="button" px={5} py={3} bg="#052E16" border="1px solid #166534"
                    borderRadius={8} fontSize="14px" fontWeight="700" color="#86EFAC"
                    fontFamily={F.ui} cursor="pointer" onClick={saveCharity}>
                    Save Charity
                  </Box>
                </Flex>
              </Box>
            </Box>
          )}

          {charities.map(c => (
            <Box key={c.id} style={cardStyle}>
              <Flex style={headerStyle} justify="space-between" align="center">
                <Flex align="center" gap={3}>
                  <Text style={{ fontFamily:F.ui, fontSize:16, fontWeight:700, color:C.white }}>❤️ {c.name}</Text>
                  <Box px={2} py={0} bg="#1A0F00" border={`1px solid ${C.gold}`} borderRadius={4}>
                    <Text style={{ fontFamily:F.mono, fontSize:9, color:C.gold }}>{c.default_pct}% DEFAULT</Text>
                  </Box>
                </Flex>
                <Box as="button" px={2} py={1} bg="transparent" border="none" fontSize="12px" color={C.red} cursor="pointer" onClick={() => deleteItem('charities', c.id)}>✕</Box>
              </Flex>
              <Box p={4}>
                {c.registration_number && <Text style={{ fontFamily:F.mono, fontSize:11, color:C.dim }}>Reg: {c.registration_number}</Text>}
                {c.notes && <Text style={{ fontFamily:F.ui, fontSize:13, color:C.ink, marginTop:4 }}>{c.notes}</Text>}
              </Box>
            </Box>
          ))}

          {charities.length === 0 && !showCharityForm && (
            <Box style={cardStyle} p={6} textAlign="center">
              <Text style={{ fontSize:36, marginBottom:8 }}>❤️</Text>
              <Text style={{ fontFamily:F.ui, fontSize:15, color:C.dim }}>No charities saved. Add a charity to split prize pools for fundraiser events.</Text>
            </Box>
          )}
        </Box>
      )}

      {/* ── LICENSES TAB ── */}
      {activeTab === 'licenses' && !loading && (
        <Box>
          <Flex justify="space-between" align="center" mb={4}>
            <Text style={{ fontFamily:F.mono, fontSize:11, color:C.dim, letterSpacing:'0.15em' }}>
              {licenses.length} LICENSE{licenses.length !== 1 ? 'S' : ''} SAVED
            </Text>
            <Box as="button" px={4} py={2} bg="#0D1F1E" border={`1px solid ${C.teal}`}
              borderRadius={6} fontSize="12px" fontWeight="700" color={C.teal}
              fontFamily={F.ui} cursor="pointer"
              onClick={() => setShowLicenseForm(v => !v)}>
              {showLicenseForm ? '✕ Cancel' : '+ Add License'}
            </Box>
          </Flex>

          {showLicenseForm && (
            <Box style={cardStyle} mb={4}>
              <Box style={headerStyle}>
                <Text style={{ fontFamily:F.mono, fontSize:11, color:C.teal, letterSpacing:'0.2em' }}>NEW LICENSE / PERMIT</Text>
              </Box>
              <Box p={4}>
                <Flex direction="column" gap={4}>
                  <Box>
                    <label style={lbl}>License Type *</label>
                    <select style={{...inp,cursor:'pointer'}} value={licenseForm.type}
                      onChange={e => setLicenseForm((f:any) => ({...f,type:e.target.value}))}>
                      {LICENSE_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
                    </select>
                  </Box>
                  <Flex gap={3}>
                    <Box flex={1}>
                      <label style={lbl}>License Number</label>
                      <input style={inp} placeholder="e.g. CGL-2024-12345"
                        value={licenseForm.number} onChange={e => setLicenseForm((f:any) => ({...f,number:e.target.value}))} />
                    </Box>
                    <Box flex={1}>
                      <label style={lbl}>Issuing Authority</label>
                      <input style={inp} placeholder="e.g. Manitoba Gaming"
                        value={licenseForm.authority} onChange={e => setLicenseForm((f:any) => ({...f,authority:e.target.value}))} />
                    </Box>
                  </Flex>
                  <Flex gap={3}>
                    <Box flex={1}>
                      <label style={lbl}>Jurisdiction</label>
                      <input style={inp} placeholder="e.g. Manitoba, Canada"
                        value={licenseForm.jurisdiction} onChange={e => setLicenseForm((f:any) => ({...f,jurisdiction:e.target.value}))} />
                    </Box>
                    <Box flex={1}>
                      <label style={lbl}>Expiry Date</label>
                      <input style={inp} type="date" value={licenseForm.expires}
                        onChange={e => setLicenseForm((f:any) => ({...f,expires:e.target.value}))} />
                    </Box>
                  </Flex>
                  <Box as="button" px={5} py={3} bg="#052E16" border="1px solid #166534"
                    borderRadius={8} fontSize="14px" fontWeight="700" color="#86EFAC"
                    fontFamily={F.ui} cursor="pointer" onClick={saveLicense}>
                    Save License
                  </Box>
                </Flex>
              </Box>
            </Box>
          )}

          {licenses.map(l => {
            const expiry = l.expires ? new Date(l.expires) : null;
            const daysLeft = expiry ? Math.ceil((expiry.getTime() - Date.now()) / 86400000) : null;
            const expColor = daysLeft === null ? C.dim : daysLeft <= 7 ? C.red : daysLeft <= 30 ? C.gold : C.green;
            return (
              <Box key={l.id} style={cardStyle}>
                <Flex style={headerStyle} justify="space-between" align="center">
                  <Flex align="center" gap={3}>
                    <Text style={{ fontFamily:F.ui, fontSize:15, fontWeight:700, color:C.white }}>📋 {l.type}</Text>
                    {daysLeft !== null && (
                      <Box px={2} py={0} bg={C.card} border={`1px solid ${expColor}`} borderRadius={4}>
                        <Text style={{ fontFamily:F.mono, fontSize:9, color:expColor }}>
                          {daysLeft <= 0 ? 'EXPIRED' : `${daysLeft}d left`}
                        </Text>
                      </Box>
                    )}
                  </Flex>
                  <Box as="button" px={2} py={1} bg="transparent" border="none" fontSize="12px" color={C.red} cursor="pointer" onClick={() => deleteItem('licenses', l.id)}>✕</Box>
                </Flex>
                <Flex p={4} gap={5} flexWrap="wrap">
                  {l.number && <Box><Text style={{ fontFamily:F.mono, fontSize:10, color:C.dim, letterSpacing:'0.12em' }}>NUMBER</Text><Text style={{ fontFamily:F.ui, fontSize:13, color:C.ink }}>{l.number}</Text></Box>}
                  {l.authority && <Box><Text style={{ fontFamily:F.mono, fontSize:10, color:C.dim, letterSpacing:'0.12em' }}>AUTHORITY</Text><Text style={{ fontFamily:F.ui, fontSize:13, color:C.ink }}>{l.authority}</Text></Box>}
                  {l.jurisdiction && <Box><Text style={{ fontFamily:F.mono, fontSize:10, color:C.dim, letterSpacing:'0.12em' }}>JURISDICTION</Text><Text style={{ fontFamily:F.ui, fontSize:13, color:C.ink }}>{l.jurisdiction}</Text></Box>}
                  {expiry && <Box><Text style={{ fontFamily:F.mono, fontSize:10, color:C.dim, letterSpacing:'0.12em' }}>EXPIRES</Text><Text style={{ fontFamily:F.ui, fontSize:13, color:expColor }}>{expiry.toLocaleDateString('en-CA',{month:'short',day:'numeric',year:'numeric'})}</Text></Box>}
                </Flex>
              </Box>
            );
          })}

          {licenses.length === 0 && !showLicenseForm && (
            <Box style={cardStyle} p={6} textAlign="center">
              <Text style={{ fontSize:36, marginBottom:8 }}>📋</Text>
              <Text style={{ fontFamily:F.ui, fontSize:15, color:C.dim }}>No licenses saved. Track your gaming licenses and permits with expiry alerts.</Text>
            </Box>
          )}
        </Box>
      )}

      {/* ── SERIES TAB ── */}
      {activeTab === 'series' && !loading && (
        <Box>
          <Flex justify="space-between" align="center" mb={4}>
            <Text style={{ fontFamily:F.mono, fontSize:11, color:C.dim, letterSpacing:'0.15em' }}>
              {tdSeries.length} RECURRING SERIES
            </Text>
            <Box as="button" px={4} py={2} bg="#0D1F1E" border={`1px solid ${C.teal}`}
              borderRadius={6} fontSize="12px" fontWeight="700" color={C.teal}
              fontFamily={F.ui} cursor="pointer"
              onClick={() => navigate('/create-event/')}>
              + Create Recurring Event →
            </Box>
          </Flex>

          {tdSeries.length === 0 ? (
            <Box style={cardStyle} p={6} textAlign="center">
              <Text style={{ fontSize:36, marginBottom:8 }}>🔄</Text>
              <Text style={{ fontFamily:F.ui, fontSize:15, color:C.dim, marginBottom:8 }}>
                No recurring series yet.
              </Text>
              <Text style={{ fontFamily:F.ui, fontSize:13, color:C.dim, lineHeight:1.7 }}>
                When you create an event and toggle Recurring in the Quick Setup panel,
                it will appear here. You can then generate the next occurrence with one tap.
              </Text>
            </Box>
          ) : tdSeries.map(s => {
            const nextDate = s.next_event_date ? new Date(s.next_event_date) : null;
            const freq = s.recurrence?.frequency ?? 'weekly';
            return (
              <Box key={s.id} style={cardStyle}>
                <Flex style={headerStyle} justify="space-between" align="center">
                  <Flex align="center" gap={3}>
                    <Text style={{ fontFamily:F.ui, fontSize:16, fontWeight:700, color:C.white }}>
                      🔄 {s.name}
                    </Text>
                    <Box px={2} py={0} bg={`${C.teal}22`} border={`1px solid ${C.teal}44`} borderRadius={4}>
                      <Text style={{ fontFamily:F.mono, fontSize:9, color:C.teal }}>
                        {freq.toUpperCase()}
                      </Text>
                    </Box>
                    {!s.active && (
                      <Box px={2} py={0} bg={`${C.red}22`} border={`1px solid ${C.red}44`} borderRadius={4}>
                        <Text style={{ fontFamily:F.mono, fontSize:9, color:C.red }}>ENDED</Text>
                      </Box>
                    )}
                  </Flex>
                </Flex>
                <Flex p={4} gap={5} flexWrap="wrap" align="center">
                  <Box>
                    <Text style={{ fontFamily:F.mono, fontSize:10, color:C.dim, letterSpacing:'0.12em' }}>OCCURRENCES</Text>
                    <Text style={{ fontFamily:F.ui, fontSize:18, fontWeight:700, color:C.white }}>
                      {s.occurrences?.length ?? 0}
                    </Text>
                  </Box>
                  <Box>
                    <Text style={{ fontFamily:F.mono, fontSize:10, color:C.dim, letterSpacing:'0.12em' }}>FOLLOWERS</Text>
                    <Text style={{ fontFamily:F.ui, fontSize:18, fontWeight:700, color:C.white }}>
                      {s.followers?.length ?? 0}
                    </Text>
                  </Box>
                  {nextDate && (
                    <Box>
                      <Text style={{ fontFamily:F.mono, fontSize:10, color:C.dim, letterSpacing:'0.12em' }}>NEXT DATE</Text>
                      <Text style={{ fontFamily:F.ui, fontSize:14, fontWeight:700, color:C.teal }}>
                        {nextDate.toLocaleDateString('en-CA', { weekday:'short', month:'short', day:'numeric' })}
                      </Text>
                    </Box>
                  )}
                  {s.active && (
                    <Box as="button" ml="auto" px={4} py={2}
                      bg="#052E16" border="1px solid #166534"
                      borderRadius={6} fontSize="13px" fontWeight="700" color="#86EFAC"
                      fontFamily={F.ui} cursor="pointer"
                      onClick={async () => {
                        const { createNextOccurrence } = await import('../../services/recurring/RecurringEventService');
                        const newId = await createNextOccurrence(s.id, user!.uid);
                        if (newId) {
                          alert('Next occurrence created! Event ID: ' + newId);
                          loadAll();
                        }
                      }}>
                      🔄 Create Next Occurrence →
                    </Box>
                  )}
                </Flex>
              </Box>
            );
          })}
        </Box>
      )}

      {/* ── PROFILE TAB ── */}
      {activeTab === 'profile' && !loading && (
        <Box>
          <Box style={cardStyle}>
            <Box style={headerStyle}>
              <Text style={{ fontFamily:F.mono, fontSize:11, color:C.teal, letterSpacing:'0.2em' }}>TD PROFILE</Text>
            </Box>
            <Box p={4}>
              <Flex direction="column" gap={4}>
                <Text style={{ fontFamily:F.ui, fontSize:14, color:C.dim, lineHeight:1.7 }}>
                  Your TD profile is pulled from your PokerCity account. To update your name, photo, or bio,
                  visit your <Box as="span" color={C.teal} cursor="pointer"
                    onClick={() => navigate(`/passport/${user.uid}`)}>Player Passport →</Box>
                </Text>
                <Flex gap={3} flexWrap="wrap">
                  {[
                    { label:'My Venues', count:venues.length, color:C.teal, tab:'venues' },
                    { label:'My Structures', count:structures.length, color:C.gold, tab:'structures' },
                    { label:'My Charities', count:charities.length, color:C.red, tab:'charities' },
                    { label:'My Licenses', count:licenses.length, color:C.green, tab:'licenses' },
                  ].map(s => (
                    <Box key={s.label} flex={1} minW="120px" bg="#0D1118"
                      border={`1px solid ${s.color}33`} borderRadius={8} p={3}
                      textAlign="center" cursor="pointer"
                      onClick={() => setActiveTab(s.tab)}>
                      <Text style={{ fontFamily:F.ui, fontSize:28, fontWeight:700, color:s.color }}>{s.count}</Text>
                      <Text style={{ fontFamily:F.mono, fontSize:9, color:C.dim, letterSpacing:'0.12em' }}>{s.label.toUpperCase()}</Text>
                    </Box>
                  ))}
                </Flex>
                <Flex gap={3} flexWrap="wrap" mt={2}>
                  {[
                    { label:'Create Event', path:'/create-event/', color:C.green },
                    { label:'Create Venue Profile', path:'/create-venue', color:C.teal },
                    { label:'Find a Dealer', path:'/find-a-dealer', color:'#A855F7' },
                    { label:'Poster Generator', path:'/poster-generator', color:C.gold },
                    { label:'PCTDS Clock', path:'/pctds-clock', color:C.red },
                  ].map(btn => (
                    <Box key={btn.label} as="button" px={4} py={2}
                      bg={C.card} border={`1px solid ${btn.color}44`}
                      borderRadius={6} fontSize="12px" fontWeight="700"
                      color={btn.color} fontFamily={F.ui} cursor="pointer"
                      onClick={() => navigate(btn.path)}>
                      {btn.label} →
                    </Box>
                  ))}
                </Flex>
              </Flex>
            </Box>
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default TDControlCenter;
